/**
 * Client portal manual — EN + PT.
 *
 * Written for JMO clients (property owners / managers) who use the portal
 * to submit service requests and follow up on projects and estimates.
 */

export const clientManual = {
  en: {
    getting_started: {
      title: "Welcome",
      path: "/",
      overview: "The JMO client portal lets you submit service requests, review estimates, approve proposals, follow project progress and see your invoice history — all in one place. This manual walks you through every feature.",
      features: [
        "Login with the email JMO configured for you.",
        "See the status of every job we're doing for you in real time.",
        "Approve estimates and proposals online.",
        "Access all invoices and payment history.",
      ],
      tasks: [
        { title: "Log in for the first time", steps: [
          "Open the portal URL provided by JMO in any browser.",
          "Use your email and the temporary password you received.",
          "You'll be asked to set a new personal password (minimum 8 characters).",
          "Bookmark the site for quick access.",
        ]},
        { title: "Reset your password", steps: [
          "If you forget your password, contact JMO support and we'll reset it for you.",
        ]},
      ],
      tips: [
        "The portal works on any device — computer, tablet, phone.",
        "Ask JMO to add other people from your team (assistants, superintendents) so more than one person can log in.",
      ],
    },

    portal_overview: {
      title: "Portal Overview",
      path: "/",
      overview: "When you log in, the dashboard highlights what needs attention — open service calls, estimates waiting for approval, invoices due. The sidebar lists all sections you can access.",
      features: [
        "Dashboard tiles: open calls, pending estimates, outstanding invoices.",
        "Sidebar menu: Service Calls, Projects, Estimates, Properties.",
        "Language toggle at the bottom of the sidebar (English / Portuguese).",
        "Notification bell (top right) shows updates from JMO.",
      ],
      tips: [
        "Enable browser notifications so you're pinged when JMO updates you.",
      ],
    },

    tickets: {
      title: "Service Calls",
      path: "/tickets",
      overview: "A Service Call is any repair or maintenance you request from JMO. This is where you open new requests and follow their progress.",
      features: [
        "'+ New' button opens the request form.",
        "Property + unit picker (or property only for single-family homes).",
        "Attach photos of the problem.",
        "Priority selector (low / medium / high / urgent).",
        "Real-time status: requested → scheduled → in progress → done.",
        "Field photos and Daily Reports from the technician.",
      ],
      tasks: [
        { title: "Open a service request", steps: [
          "Sidebar → Service Calls → '+ New'.",
          "Fill in the Title (e.g., 'Water leak in bathroom').",
          "Describe the issue — the more detail, the faster we can dispatch.",
          "Pick the Property (and the Unit, if applicable).",
          "Set priority. Use 'urgent' only for true emergencies (water, gas, no heat).",
          "Attach photos.",
          "Save — JMO is notified immediately.",
        ]},
        { title: "Follow up on a request", steps: [
          "Open the ticket to see current status.",
          "Once the technician arrives, you'll see photos and notes in real time.",
          "At the end of the day you'll receive a Daily Report by email with everything documented.",
        ]},
      ],
      tips: [
        "Attach at least one photo per request — even a bad photo helps the tech prepare.",
        "The tenant's phone number is important for scheduling — always include it if applicable.",
        "'Urgent' priority is for water, gas, and no-heat emergencies. Please respect this so real emergencies get through.",
      ],
      troubleshooting: [
        "'Nobody responded': check the ticket status. If it's still 'requested', call the office.",
      ],
    },

    projects: {
      title: "Projects",
      path: "/projects",
      overview: "Projects are large multi-phase engagements — building rehabs, boiler replacements, energy retrofits. Unlike single service calls, projects have a contract value and are billed in phases as work progresses.",
      features: [
        "Contract value and remaining balance.",
        "Progress % per line item (Work Breakdown Structure).",
        "Every progress invoice is visible here.",
        "Approve proposals directly with a signature link.",
        "Daily Reports and photos aggregated in one place.",
      ],
      tasks: [
        { title: "Approve a proposal", steps: [
          "You'll receive an email with a secure link.",
          "Review the scope, price, terms.",
          "Approve or request changes online — no printing, no scanning.",
        ]},
        { title: "Review project progress", steps: [
          "Sidebar → Projects → click the project.",
          "The WBS shows each line item with % complete.",
          "The Invoices tab lists all progress invoices with paid/unpaid status.",
        ]},
      ],
      tips: [
        "Weekly Daily Reports from the site are the fastest way to know how the project is going.",
      ],
    },

    estimates: {
      title: "Estimates",
      path: "/estimates",
      overview: "Estimates are quotes JMO sends you for approval before work starts. Any Service Call marked 'Requires estimate' gets one automatically.",
      features: [
        "PDF version you can save and share.",
        "One-click approval or rejection.",
        "Comments back to JMO on any line item.",
      ],
      tasks: [
        { title: "Approve an estimate", steps: [
          "Sidebar → Estimates → click the pending estimate.",
          "Review each line, unit price, and total.",
          "'Approve' turns it into a scheduled Service Call automatically.",
          "'Reject' with a comment sends it back to JMO for revision.",
        ]},
      ],
      tips: [
        "Ask questions in the comments before approving — it's faster than email back-and-forth.",
      ],
    },

    properties: {
      title: "Properties",
      path: "/properties",
      overview: "Your buildings and units. Every service call is tied to one of these. Keep the address, tenant info, and access codes up to date so technicians don't get delayed.",
      features: [
        "Property registry with address, city, ZIP.",
        "Nested Units (apartments, floors).",
        "Tenant contact per unit.",
        "Access notes (key location, alarm code, dog on premises).",
      ],
      tasks: [
        { title: "Add a new property", steps: [
          "Sidebar → Properties → '+ New'.",
          "Enter address and property name.",
          "If it has units (apartments, floors), add them.",
          "Save — you can now open Service Calls for it.",
        ]},
        { title: "Update a tenant contact", steps: [
          "Open the unit → edit the tenant name / phone.",
          "Future service calls will use this contact automatically.",
        ]},
      ],
      tips: [
        "Keep access codes current — nothing wastes time like a tech showing up and not being able to enter.",
        "Add a 'Notes' entry for special conditions (aggressive dog, elderly tenant, etc.) — it protects everyone.",
      ],
    },
  },

  pt: {
    getting_started: {
      title: "Bem-vindo",
      path: "/",
      overview: "O portal do cliente JMO permite abrir chamados de serviço, revisar estimativas, aprovar propostas, acompanhar projetos em andamento e ver histórico de faturas — tudo num só lugar. Este manual guia você por cada funcionalidade.",
      features: [
        "Login com o email que a JMO configurou.",
        "Status em tempo real de todo trabalho que estamos fazendo pra você.",
        "Aprove estimativas e propostas online.",
        "Acesso a todas as faturas e histórico de pagamentos.",
      ],
      tasks: [
        { title: "Fazer login pela primeira vez", steps: [
          "Abra a URL do portal enviada pela JMO em qualquer navegador.",
          "Use seu email e a senha temporária recebida.",
          "Você será pedido a criar uma nova senha pessoal (mínimo 8 caracteres).",
          "Salve o site nos favoritos pra acesso rápido.",
        ]},
        { title: "Resetar senha", steps: [
          "Se esqueceu a senha, contate o suporte JMO e a gente reseta pra você.",
        ]},
      ],
      tips: [
        "O portal funciona em qualquer dispositivo — computador, tablet, celular.",
        "Peça à JMO pra adicionar outras pessoas do seu time (assistentes, síndicos), assim mais de um logam.",
      ],
    },

    portal_overview: {
      title: "Visão Geral do Portal",
      path: "/",
      overview: "Ao fazer login, o dashboard destaca o que precisa da sua atenção — chamados abertos, estimativas aguardando aprovação, faturas vencendo. O menu lateral lista todas as seções às quais você tem acesso.",
      features: [
        "Dashboard: chamados abertos, estimativas pendentes, faturas em aberto.",
        "Menu lateral: Service Calls, Projects, Estimates, Properties.",
        "Alternador de idioma no rodapé do menu (Inglês / Português).",
        "Sininho de notificações (canto superior direito) mostra atualizações da JMO.",
      ],
      tips: [
        "Ative as notificações do navegador pra receber ping quando a JMO atualizar algo.",
      ],
    },

    tickets: {
      title: "Service Calls (Chamados)",
      path: "/tickets",
      overview: "Um Service Call é qualquer reparo ou manutenção que você solicita à JMO. É aqui que você abre novos chamados e acompanha o andamento.",
      features: [
        "Botão '+ New' abre o formulário de solicitação.",
        "Seletor Property + Unit (ou só Property pra casas).",
        "Anexar fotos do problema.",
        "Prioridade (low / medium / high / urgent).",
        "Status em tempo real: requested → scheduled → in progress → done.",
        "Fotos e Daily Reports do técnico em campo.",
      ],
      tasks: [
        { title: "Abrir chamado", steps: [
          "Menu → Service Calls → '+ New'.",
          "Preencha o Title (ex.: 'Vazamento no banheiro').",
          "Descreva o problema — quanto mais detalhe, mais rápido despachamos.",
          "Escolha a Property (e a Unit, se aplicável).",
          "Defina prioridade. Use 'urgent' só pra emergências reais (água, gás, sem aquecimento).",
          "Anexe fotos.",
          "Salve — JMO é notificado imediatamente.",
        ]},
        { title: "Acompanhar solicitação", steps: [
          "Abra o ticket pra ver o status atual.",
          "Quando o técnico chegar, você vê fotos e notas em tempo real.",
          "No fim do dia você recebe o Daily Report por email com tudo documentado.",
        ]},
      ],
      tips: [
        "Anexe pelo menos uma foto por chamado — mesmo foto ruim ajuda o técnico a se preparar.",
        "Telefone do inquilino é importante pra agendamento — sempre inclua se aplicável.",
        "'Urgent' é pra emergências de água, gás e sem calor. Respeite isso pra emergências reais não ficarem enfileiradas.",
      ],
      troubleshooting: [
        "'Ninguém respondeu': confira o status do ticket. Se ainda estiver 'requested', ligue pro escritório.",
      ],
    },

    projects: {
      title: "Projects",
      path: "/projects",
      overview: "Projects são obras grandes multi-fase — reforma de prédio, troca de boiler, retrofit energético. Diferente de chamados simples, projetos têm valor de contrato e são faturados em fases conforme o trabalho avança.",
      features: [
        "Valor de contrato e saldo restante.",
        "% de progresso por linha (Work Breakdown Structure).",
        "Todas as invoices de progresso ficam visíveis aqui.",
        "Aprove propostas direto com link seguro (sem imprimir).",
        "Daily Reports e fotos consolidados num só lugar.",
      ],
      tasks: [
        { title: "Aprovar proposta", steps: [
          "Você recebe email com link seguro.",
          "Revise escopo, preço, condições.",
          "Aprove ou peça mudança online — sem impressora, sem scanner.",
        ]},
        { title: "Revisar progresso do projeto", steps: [
          "Menu → Projects → clique no projeto.",
          "O WBS mostra cada linha com % completo.",
          "A aba Invoices lista todas as faturas de progresso com status pago/não-pago.",
        ]},
      ],
      tips: [
        "Daily Reports semanais do site são a forma mais rápida de saber como o projeto está indo.",
      ],
    },

    estimates: {
      title: "Estimates",
      path: "/estimates",
      overview: "Estimates são orçamentos que a JMO envia pra sua aprovação antes de começar o trabalho. Qualquer Service Call marcado como 'Requires estimate' gera um automaticamente.",
      features: [
        "Versão em PDF pra salvar/compartilhar.",
        "Aprovação ou rejeição com 1 clique.",
        "Comentários por linha de volta pra JMO.",
      ],
      tasks: [
        { title: "Aprovar estimate", steps: [
          "Menu → Estimates → clique no estimate pendente.",
          "Revise cada linha, unit price e total.",
          "'Approve' vira Service Call agendado automaticamente.",
          "'Reject' com comentário volta pra JMO revisar.",
        ]},
      ],
      tips: [
        "Faça perguntas nos comentários antes de aprovar — mais rápido que troca de emails.",
      ],
    },

    properties: {
      title: "Properties (Imóveis)",
      path: "/properties",
      overview: "Seus prédios e units. Todo Service Call se amarra a um desses. Mantenha o endereço, contato do inquilino e códigos de acesso atualizados pra técnicos não se atrasarem.",
      features: [
        "Cadastro de property com endereço, cidade, ZIP.",
        "Units aninhadas (apartamentos, andares).",
        "Contato do inquilino por unit.",
        "Notas de acesso (onde está a chave, código do alarme, cachorro).",
      ],
      tasks: [
        { title: "Adicionar nova property", steps: [
          "Menu → Properties → '+ New'.",
          "Endereço e nome da property.",
          "Se tiver units (aptos, andares), adicione.",
          "Salve — já pode abrir Service Calls pra ela.",
        ]},
        { title: "Atualizar contato do inquilino", steps: [
          "Abra a unit → edite nome/telefone do inquilino.",
          "Chamados futuros vão usar esse contato automaticamente.",
        ]},
      ],
      tips: [
        "Mantenha códigos de acesso atualizados — nada perde mais tempo do que técnico chegar e não conseguir entrar.",
        "Adicione uma 'Note' pra condições especiais (cachorro agressivo, inquilino idoso, etc.) — protege todo mundo.",
      ],
    },
  },
};
